import { useMemo } from "react";
import {
  findSymbolData,
  formatTrimmedNumber,
  inferSymbolPricePrecision,
  splitTradeSymbol,
} from "../tradeUtils";

export default function Tableau({ marketData, symbol, className = "" }) {
  const selectedSymbolData = useMemo(
    () => findSymbolData(marketData?.symbols, symbol),
    [marketData, symbol],
  );

  const buyRows = useMemo(() => {
    if (!selectedSymbolData?.book?.bids) return [];
    return selectedSymbolData.book.bids.map((item) => ({
      price: item?.price,
      amount: item?.qty,
    }));
  }, [selectedSymbolData]);

  const sellRows = useMemo(() => {
    if (!selectedSymbolData?.book?.asks) return [];
    return selectedSymbolData.book.asks.map((item) => ({
      price: item?.price,
      amount: item?.qty,
    }));
  }, [selectedSymbolData]);

  const allRows = [...buyRows, ...sellRows];

  const nowPrice = selectedSymbolData?.last;
  const priceDecimals = inferSymbolPricePrecision(selectedSymbolData, 4);
  const baseAsset = splitTradeSymbol(selectedSymbolData?.symbol || symbol).base;

  const calcAmountStats = (rows) => {
    const values = rows
      .map((row) => Number(row.amount))
      .filter((value) => Number.isFinite(value));

    if (!values.length) return { min: 0, range: 1 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    return {
      min,
      range: max - min || 1,
    };
  };

  const buyStats = useMemo(() => calcAmountStats(buyRows), [buyRows]);
  const sellStats = useMemo(() => calcAmountStats(sellRows), [sellRows]);
  const allStats = useMemo(() => calcAmountStats(allRows), [allRows]);

  const formatPrice = (price, fallback = "") => {
    if (price === null || price === undefined || price === "") return fallback;

    const value = Number(price);
    if (!Number.isFinite(value)) return fallback;

    return value.toLocaleString("en-US", {
      useGrouping: false,
      minimumFractionDigits: priceDecimals,
      maximumFractionDigits: priceDecimals,
    });
  };

  const formatInputPrice = (price, fallback = "") =>
    formatTrimmedNumber(price, {
      useGrouping: false,
      fallback,
      maxFractionDigits: priceDecimals,
    });

  const formatAmount = (amount, fallback = "-") =>
    formatTrimmedNumber(amount, {
      useGrouping: false,
      fallback,
      maxFractionDigits: 8,
    });

  // Helper: copy price to appropriate input based on order type
  const handlePriceClick = (price) => {
    if (typeof document === "undefined") return;

    let targetId = "price"; // default for Limit

    const input = document.getElementById(targetId);
    if (input) {
      // use plain numeric string without thousand separators
      input.value = formatInputPrice(price);
      // dispatch input event so React-controlled components can pick it up
      const event = new Event("input", { bubbles: true });
      input.dispatchEvent(event);
    }
  };

  return (
    <div
      className={`w-full h-full p-2.5 flex flex-col justify-evenly rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark ${className}`}
    >
      
      {/* table sell */}
      <div className=" flex flex-col gap-2.5 divide-y divide-zi-border-dark/50 dark:divide-zi-border-light/50">
        {/* header sell */}
        <div className="w-full flex gap-2.5">
          <span className="w-full text-center text-nowrap text-xs sm:text-sm text-zi-text-light/50 dark:text-zi-text-dark/50 flex items-center justify-center gap-1">
            قیمت 
            <span className="hidden sm:inline">(USDT)</span>
          </span>
          <span className="w-full text-center text-nowrap text-xs sm:text-sm text-zi-text-light/50 dark:text-zi-text-dark/50 flex items-center justify-center gap-1">
            حجم 
            <span className="hidden sm:inline">({baseAsset || "-"})</span>
          </span>
        </div>
        {/* values sell */}
        <div className="w-full flex flex-col gap-1 pt-2.5 overflow-x-auto">
          {sellRows.slice().reverse().map((row, index) => {
            const amountValue = Number(row.amount);
            const normalizedSell = Number.isFinite(amountValue)
              ? (amountValue - allStats.min) / allStats.range
              : 0;
            // Map to 10% (min) -> 90% (max)
            const fillPercentSell = 10 + normalizedSell * 90;

            const bgStyleSell = {
              background: `linear-gradient(
                to left,
                rgba(215, 31, 31, 0.15) 0%,                      /* strong red at the right */
                transparent ${fillPercentSell}%                  /* end of fade */
              )`,
            };

            return (
              <div
                key={index}
                className="w-full flex gap-2 py-1.5 rounded-md"
                style={bgStyleSell}
              >
                <span
                  onClick={() => handlePriceClick(row.price)}
                  className="w-full text-center text-xs sm:text-sm font-medium text-zi-red-dark cursor-pointer"
                >
                  {formatPrice(row.price, "-")}
                </span>
                <span className="w-full text-center text-xs sm:text-sm text-zi-text-light dark:text-zi-text-dark">
                  {formatAmount(row.amount)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* live price */}
      <div className="flex flex-col gap-0 py-2.5">
        <div className="w-full flex">
          <span className="w-full text-center whitespace-nowrap text-xs text-zi-text-light/50 dark:text-zi-text-dark/50">
            قیمت لحظه‌ای
          </span>
        </div>
        <div className="w-full flex flex-col">
          {nowPrice !== null && nowPrice !== undefined ? (
            <>
              <span className="w-fit max-w-full self-center">
                <span
                  onClick={() => handlePriceClick(nowPrice)}
                  className="w-full text-center text-lg font-medium text-zi-text-light dark:text-zi-text-dark cursor-pointer flex items-center justify-start gap-1 overflow-x-auto [direction:ltr]"
                >
                  {formatPrice(nowPrice, "-")}
                  <span className="text-xs font-extralight text-zi-text-light/50 dark:text-zi-text-dark/50">(USDT)</span> 
                </span>
              </span>
              {/* <span className="w-full text-center text-sm text-zi-text-light dark:text-zi-text-dark [direction:ltr]">
                = ${formatPrice(nowPrice, "-")}
              </span> */}
            </>
          ) : (
            <span className="w-full text-center text-2xl font-medium text-zi-text-light dark:text-zi-text-dark">
              -
            </span>
          )}
        </div>
      </div>

      {/* table buy */}
      <div className=" flex flex-col gap-2.5 divide-y divide-zi-border-dark/50 dark:divide-zi-border-light/50">
        {/* header buy */}
        <div className="w-full flex gap-2.5">
          <span className="w-full text-center text-nowrap text-xs sm:text-sm text-zi-text-light/50 dark:text-zi-text-dark/50 flex items-center justify-center gap-1">
            قیمت
            <span className="hidden sm:inline">(USDT)</span>
          </span>
          <span className="w-full text-center text-nowrap text-xs sm:text-sm text-zi-text-light/50 dark:text-zi-text-dark/50 flex items-center justify-center gap-1">
            حجم
            <span className="hidden sm:inline">({baseAsset || "-"})</span>
          </span>
        </div>
        {/* values buy */}
        <div className="w-full flex flex-col gap-1 pt-2.5 overflow-x-auto">
          {buyRows.map((row, index) => {
            const amountValue = Number(row.amount);
            const normalizedBuy = Number.isFinite(amountValue)
              ? (amountValue - allStats.min) / allStats.range
              : 0;
            // Map to 10% (min) -> 90% (max)
            const fillPercentBuy = 10 + normalizedBuy * 90;

            const bgStyleBuy = {
              background: `linear-gradient(
                to left,
                rgba(21, 169, 70, 0.15) 0%,                      /* strong green at the right */
                transparent ${fillPercentBuy}%                   /* end of fade */
              )`,
            };

            return (
              <div
                key={index}
                className="w-full flex gap-2 py-1.5 rounded-md"
                style={bgStyleBuy}
              >
                <span
                  onClick={() => handlePriceClick(row.price)}
                  className="w-full text-center text-xs sm:text-sm font-medium text-zi-green-dark cursor-pointer"
                >
                  {formatPrice(row.price, "-")}
                </span>
                <span className="w-full text-center text-xs sm:text-sm text-zi-text-light dark:text-zi-text-dark">
                  {formatAmount(row.amount)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
